import { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Calendar, CheckCircle, Clock, AlertCircle, XCircle,
  Globe, X, Send, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function ClientSubscriptions() {
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptLoading, setAcceptLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedSubId, setSelectedSubId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/my-subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // ---- Accept admin proposal ----
  const acceptProposal = async (sub) => {
    setAcceptLoading(sub.id);
    try {
      const res = await fetch(`${API_URL}/api/payments/subscriptions/${sub.id}/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Origin: window.location.origin,
        },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Erreur lors de la souscription');
      }
    } catch (err) {
      toast.error('Erreur lors de la souscription');
    } finally {
      setAcceptLoading(null);
    }
  };

  // ---- Open Stripe billing portal to update payment method ----
  const openBillingPortal = async (sub) => {
    setPortalLoading(sub.id);
    try {
      const res = await fetch(`${API_URL}/api/payments/subscriptions/${sub.id}/billing-portal`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Origin: window.location.origin,
        },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Impossible d\'ouvrir le portail Stripe');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'ouverture du portail');
    } finally {
      setPortalLoading(null);
    }
  };

  const openCancelModal = (subId) => {
    setSelectedSubId(subId);
    setShowCancelModal(true);
  };

  const handleRequestCancellation = async () => {
    if (!selectedSubId) return;
    setCancelLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/subscriptions/${selectedSubId}/request-cancellation`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: cancelReason || null }),
      });
      if (res.ok) {
        setShowCancelModal(false);
        setCancelReason('');
        setSelectedSubId(null);
        toast.success('Demande de résiliation envoyée à l\'équipe Axeris');
        fetchSubscriptions();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Erreur lors de la demande');
      }
    } catch (err) {
      toast.error('Erreur lors de la demande');
    } finally {
      setCancelLoading(false);
    }
  };

  // ---- Filter ----
  const proposed = subscriptions.filter((s) => s.status === 'proposed');
  const active = subscriptions.filter((s) => s.status === 'active' || s.status === 'cancellation_requested');

  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      proposed: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      cancellation_requested: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700',
    };
    const labels = {
      active: 'Actif',
      proposed: 'À souscrire',
      pending: 'En attente',
      cancellation_requested: 'Résiliation demandée',
      cancelled: 'Résilié',
      expired: 'Expiré',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8" data-testid="client-subscriptions">
      <div>
        <h1 className="text-3xl font-bold mb-2">Hébergement</h1>
        <p className="text-muted-foreground">
          Les abonnements d'hébergement sont proposés directement par Axeris Labs. Pour toute demande, contactez l'équipe.
        </p>
      </div>

      {/* ============ PROPOSITIONS À SOUSCRIRE ============ */}
      {proposed.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" /> Propositions en attente
          </h2>
          {proposed.map((sub) => (
            <Card key={sub.id} className="border-blue-300/70 bg-blue-50/30" data-testid={`proposed-${sub.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <Globe className="h-7 w-7 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{sub.name || 'Hébergement'}</h3>
                        <StatusBadge status="proposed" />
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Hébergement mensuel proposé par Axeris Labs
                      </p>
                      <p className="text-2xl font-bold text-blue-700 mt-2">
                        {Number(sub.amount).toFixed(2)} €<span className="text-sm text-muted-foreground font-normal">/mois</span>
                      </p>
                      {sub.admin_notes && (
                        <p className="text-xs text-muted-foreground mt-2 italic">{sub.admin_notes}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => acceptProposal(sub)}
                    disabled={acceptLoading === sub.id}
                    className="btn-primary"
                    data-testid={`subscribe-${sub.id}`}
                  >
                    {acceptLoading === sub.id ? 'Redirection…' : 'Souscrire'}
                    <CreditCard className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ============ ABONNEMENTS ACTIFS ============ */}
      {active.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Mes abonnements actifs</h2>
          {active.map((sub) => (
            <Card key={sub.id} className="border-primary/50" data-testid={`sub-${sub.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${sub.status === 'active' ? 'bg-green-100' : 'bg-orange-100'}`}>
                      {sub.status === 'active' ? (
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      ) : (
                        <Clock className="h-8 w-8 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{sub.name || 'Hébergement'}</h3>
                        <StatusBadge status={sub.status} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {Number(sub.amount).toFixed(2)} €/mois
                      </p>
                      {sub.current_period_end && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {sub.status === 'active'
                            ? `Renouvellement le ${new Date(sub.current_period_end).toLocaleDateString('fr-FR')}`
                            : 'Résiliation en cours de traitement'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sub.status === 'active' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openBillingPortal(sub)}
                          disabled={portalLoading === sub.id}
                          data-testid={`update-payment-${sub.id}`}
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          {portalLoading === sub.id ? 'Ouverture…' : 'MAJ paiement'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCancelModal(sub.id)}
                          className="text-destructive border-destructive hover:bg-destructive/10"
                          data-testid={`cancel-${sub.id}`}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Demander résiliation
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ============ EMPTY STATE ============ */}
      {proposed.length === 0 && active.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
            <h3 className="text-lg font-semibold mb-2">Aucun abonnement</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Vous n'avez pas encore d'abonnement d'hébergement. Axeris Labs vous proposera un abonnement adapté à votre projet : il apparaîtra ici dès qu'il sera disponible.
            </p>
            <p className="text-xs text-muted-foreground mt-4">
              Une question ? Contactez-nous à{' '}
              <a href="mailto:contact@axeris-labs.fr" className="text-primary hover:underline">
                contact@axeris-labs.fr
              </a>
            </p>
          </CardContent>
        </Card>
      )}

      {/* ============ CANCEL MODAL ============ */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  Demander la résiliation
                </h3>
                <button onClick={() => setShowCancelModal(false)} className="p-1 hover:bg-muted rounded">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Votre demande sera transmise à l'équipe Axeris. Vous serez recontacté pour confirmer.
              </p>
              <label className="text-sm font-medium block mb-2">Motif (optionnel)</label>
              <Input
                placeholder="Ex. site fermé, changement de projet…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mb-4"
                data-testid="cancel-reason"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCancelModal(false)}>Annuler</Button>
                <Button
                  onClick={handleRequestCancellation}
                  disabled={cancelLoading}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="confirm-cancel"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {cancelLoading ? 'Envoi…' : 'Envoyer la demande'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
